// src/corporativo/ComprasEstoque.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaBoxOpen, FaShoppingCart, FaSearch, FaPlus,
  FaExclamationCircle, FaExchangeAlt, FaArrowUp, FaArrowDown,
  FaPaperclip, FaCheckCircle, FaPaperPlane, FaSave, FaTimes,
  FaCubes, FaClipboardList, FaEdit, FaSpinner, FaEye, FaBuilding, FaTag
} from 'react-icons/fa';
import './EnterpriseModules.css';

const toNumber = (val, def = 0) => {
  if (val === null || val === undefined) return def;
  const s = typeof val === 'string' ? String(val).replace(',', '.') : String(val);
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : def;
};
const formatErr = (err) => err?.response?.data?.message || err?.message || 'Ocorreu um erro inesperado.';

const ModalShell = ({ open, title, onClose, children, footer, maxWidth = 600 }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ent-modal-backdrop fade-in" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 9999 }}>
      <div className="ent-modal fade-in-up" style={{ width: '100%', maxWidth, backgroundColor: '#f8fafc', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="ent-modal-header" style={{ padding: '20px 25px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#94a3b8', cursor: 'pointer', display: 'flex' }}><FaTimes /></button>
        </div>
        <div className="ent-modal-body" style={{ padding: '25px', overflowY: 'auto', flexGrow: 1 }}>{children}</div>
        {footer && (
          <div className="ent-modal-footer" style={{ padding: '15px 25px', borderTop: '1px solid #e2e8f0', backgroundColor: '#fff', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ComprasEstoque() {
  const [activeTab, setActiveTab] = useState('produtos');

  const [produtos, setProdutos] = useState([]);
  const [requisicoes, setRequisicoes] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);

  const [loadingList, setLoadingList] = useState(false);
  const [savingReq, setSavingReq] = useState(false);
  const [savingAdjust, setSavingAdjust] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  const [isReqModalOpen, setIsReqModalOpen] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [reqDetailsModalOpen, setReqDetailsModalOpen] = useState(false); // ✅ Modal de Rastreio

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedReq, setSelectedReq] = useState(null); // ✅ Guardar a Req selecionada
  const [adjustFormData, setAdjustFormData] = useState({ tipo: 'saida', quantidade: 1, motivo: '', custo_unitario: '' });
  const [adjustFile, setAdjustFile] = useState(null);

  const [formRequisicao, setFormRequisicao] = useState({ descricao: '', prioridade: 'media', centro_custo: '' });
  const [formEditProduto, setFormEditProduto] = useState({ nome: '', codigo_sku: '', categoria: '', unidade_medida: '', estoque_minimo: '' });

  useEffect(() => {
    if (activeTab === 'produtos') fetchProdutos();
    if (activeTab === 'requisicoes') fetchRequisicoes();
  }, [activeTab]);

  useEffect(() => {
    const fetchCCs = async () => {
      try {
        const { data } = await api.get('/api/corporativo/centros-custo');
        const ccs = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setCentrosCusto(ccs.filter(c => c.status === 'ativo'));
      } catch (e) {
        console.error('Erro ao buscar centros de custo:', e);
      }
    };
    fetchCCs();
  }, []);

  const fetchProdutos = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/api/corporativo/estoque/produtos');
      setProdutos(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(`Erro ao carregar estoque: ${formatErr(error)}`);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchRequisicoes = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/api/corporativo/estoque/requisicoes');
      setRequisicoes(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(`Erro ao carregar requisições: ${formatErr(error)}`);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSaveRequisicao = async () => {
    const payload = { 
      descricao: (formRequisicao.descricao || '').trim(), 
      prioridade: formRequisicao.prioridade || 'media', 
      centro_custo: (formRequisicao.centro_custo || '').trim() 
    };
    if (!payload.descricao) return toast.warn('Descreva o que precisa comprar.');

    setSavingReq(true);
    try {
      await api.post('/api/corporativo/estoque/requisicoes', payload);
      toast.success('Requisição enviada com sucesso!');
      setIsReqModalOpen(false);
      setFormRequisicao({ descricao: '', prioridade: 'media', centro_custo: '' });
      fetchRequisicoes();
    } catch (error) {
      toast.error(`Erro ao criar requisição: ${formatErr(error)}`);
    } finally {
      setSavingReq(false);
    }
  };

  const openEditModal = (produto) => {
    setSelectedProduct(produto);
    setFormEditProduto({
      nome: produto.nome || '',
      codigo_sku: produto.codigo_sku || '',
      categoria: produto.categoria || '',
      unidade_medida: produto.unidade_medida || 'UN',
      estoque_minimo: produto.estoque_minimo || 0
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!formEditProduto.nome) return toast.warn('O nome é obrigatório.');
    
    setSavingEdit(true);
    try {
      await api.put(`/api/corporativo/estoque/produtos/${selectedProduct.id}`, formEditProduto);
      toast.success('Informações do produto atualizadas!');
      setIsEditModalOpen(false);
      fetchProdutos();
    } catch (error) {
      toast.error(`Erro ao atualizar: ${formatErr(error)}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const openAdjustModal = (produto) => {
    setSelectedProduct(produto);
    setAdjustFormData({ tipo: 'saida', quantidade: 1, motivo: '', custo_unitario: '' });
    setAdjustFile(null);
    setIsAdjustModalOpen(true);
  };

  const openReqDetails = (req) => {
    setSelectedReq(req);
    setReqDetailsModalOpen(true);
  };

  const handleSaveAdjustment = async () => {
    if (!selectedProduct) return;

    const qtd = toNumber(adjustFormData.quantidade, 0);
    if (qtd <= 0) return toast.warn('Informe uma quantidade válida.');

    const saldoAtual = toNumber(selectedProduct.estoque_atual, 0);
    if (adjustFormData.tipo === 'saida' && qtd > saldoAtual) {
      return toast.warn('Não há saldo suficiente para essa saída!');
    }

    if (adjustFormData.tipo === 'saida') {
      const ok = window.confirm(`Confirmar BAIXA de ${qtd} ${selectedProduct.unidade_medida} do estoque?`);
      if (!ok) return;
    }

    setSavingAdjust(true);
    try {
      const data = new FormData();
      data.append('produto_id', selectedProduct.id);
      data.append('tipo', adjustFormData.tipo);
      data.append('quantidade', String(qtd));
      data.append('motivo', adjustFormData.motivo || '');

      if (adjustFormData.tipo === 'entrada') {
        const custo = toNumber(adjustFormData.custo_unitario, 0);
        if (custo > 0) data.append('custo_unitario', String(custo));
      }
      if (adjustFile) data.append('anexo', adjustFile);

      await api.post('/api/corporativo/estoque/movimentar', data);
      toast.success('Movimentação registrada com sucesso!');
      setIsAdjustModalOpen(false);
      setAdjustFile(null);
      fetchProdutos();
    } catch (error) {
      toast.error(`Erro ao movimentar estoque: ${formatErr(error)}`);
    } finally {
      setSavingAdjust(false);
    }
  };

  const isProdutoCritico = (curStr, minStr) => {
    const cur = toNumber(curStr, 0);
    const min = toNumber(minStr, 0);
    return (cur <= min && min > 0) || (cur === 0 && min === 0);
  };

  const StockHealthBar = ({ current, min }) => {
    const cur = toNumber(current, 0);
    const minimum = toNumber(min, 0); 
    
    const maxCapacity = minimum === 0 ? Math.max(cur * 2, 10) : minimum * 3;
    const percentage = Math.min((cur / maxCapacity) * 100, 100);

    let colorClass = '#10b981'; 
    let bgClass = '#ecfdf5';
    let statusText = 'Saudável';

    if (isProdutoCritico(current, min)) {
      colorClass = '#ef4444'; 
      bgClass = '#fef2f2';
      statusText = 'Crítico';
    } else if (cur <= minimum * 1.5 && minimum > 0) {
      colorClass = '#f59e0b'; 
      bgClass = '#fffbeb';
      statusText = 'Atenção';
    }

    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '6px', fontWeight: 600 }}>
          <span style={{ color: '#475569' }}>Qtd: <strong style={{ color: '#0f172a', fontSize: '0.95rem' }}>{cur}</strong> <span style={{ fontWeight: 500, color: '#94a3b8' }}>(Mín: {minimum})</span></span>
          <span style={{ color: colorClass, backgroundColor: bgClass, padding: '2px 8px', borderRadius: '10px' }}>{statusText}</span>
        </div>
        <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '99px', height: '8px', overflow: 'hidden' }}>
          <div style={{ width: `${percentage}%`, backgroundColor: colorClass, height: '100%', borderRadius: '99px', transition: 'width 0.5s ease-out' }} />
        </div>
      </div>
    );
  };

  const totalCriticos = produtos.filter(p => isProdutoCritico(p.estoque_atual, p.estoque_minimo)).length;

  const filteredProdutos = useMemo(() => {
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) return produtos;
    return produtos.filter(p =>
      (p?.nome || '').toLowerCase().includes(term) ||
      (p?.codigo_sku || '').toLowerCase().includes(term) ||
      (p?.categoria || '').toLowerCase().includes(term)
    );
  }, [searchTerm, produtos]);

  return (
    <div className="ent-container" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.8rem' }}>
            <FaCubes color="#4f46e5" /> Gestão de Inventário
          </h1>
          <p style={{ margin: '5px 0 0 0', color: '#64748b' }}>
            Controle de saldos, consumo interno e acompanhamento de requisições enviadas ao Depto de Compras.
          </p>
        </div>
        <div style={{ display: 'flex', background: '#f1f5f9', padding: '5px', borderRadius: '10px' }}>
          <button onClick={() => setActiveTab('produtos')} style={{ border: 'none', padding: '8px 20px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', backgroundColor: activeTab === 'produtos' ? '#fff' : 'transparent', color: activeTab === 'produtos' ? '#4f46e5' : '#64748b', boxShadow: activeTab === 'produtos' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}>
            Saldo de Estoque
          </button>
          <button onClick={() => setActiveTab('requisicoes')} style={{ border: 'none', padding: '8px 20px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', backgroundColor: activeTab === 'requisicoes' ? '#fff' : 'transparent', color: activeTab === 'requisicoes' ? '#4f46e5' : '#64748b', boxShadow: activeTab === 'requisicoes' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}>
            Requisições Internas
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '25px' }}>
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', borderLeft: '5px solid #4f46e5', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Itens no Inventário</p>
              <h3 style={{ margin: '5px 0 0 0', color: '#0f172a', fontSize: '1.8rem' }}>{produtos.length}</h3>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#e0e7ff', borderRadius: '50%' }}><FaBoxOpen color="#4f46e5" size={24}/></div>
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', borderLeft: '5px solid #ef4444', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, color: '#ef4444', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Estoque Crítico</p>
              <h3 style={{ margin: '5px 0 0 0', color: '#ef4444', fontSize: '1.8rem' }}>{totalCriticos}</h3>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '50%' }}><FaExclamationCircle color="#ef4444" size={24}/></div>
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', borderLeft: '5px solid #10b981', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, color: '#047857', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Pedidos Gerados</p>
              <h3 style={{ margin: '5px 0 0 0', color: '#047857', fontSize: '1.8rem' }}>{requisicoes.filter(r => r?.status === 'atendido' || !!r?.numero_po).length}</h3>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '50%' }}><FaShoppingCart color="#10b981" size={24}/></div>
          </div>
        </div>
      </div>

      {activeTab === 'produtos' && (
        <div className="fade-in-up">
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <FaSearch style={{ position: 'absolute', top: '12px', left: '14px', color: '#94a3b8' }} />
              <input style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.95rem', outline: 'none' }} placeholder="Buscar por Nome, SKU ou Categoria..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            {loadingList ? (
              <div style={{ padding: '50px', textAlign: 'center' }}><FaSpinner className="fa-spin" size={35} color="#4f46e5" /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Mercadoria / Identificação</th>
                      <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Categoria</th>
                      <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', width: '30%' }}>Indicador de Saldo</th>
                      <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center' }}>Gestão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProdutos.map(prod => (
                      <tr key={prod.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: '15px 20px' }}>
                          <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '1.05rem', marginBottom: '6px' }}>{prod?.nome}</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ backgroundColor: prod?.codigo_sku ? '#e0f2fe' : '#fef2f2', color: prod?.codigo_sku ? '#0369a1' : '#dc2626', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontWeight: 600 }}>
                              {prod?.codigo_sku ? `SKU: ${prod.codigo_sku}` : 'SEM SKU'}
                            </span>
                            <span>Unid: <strong>{prod?.unidade_medida}</strong></span>
                          </div>
                        </td>
                        <td style={{ padding: '15px 20px' }}>
                          <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600, border: '1px solid #e2e8f0' }}>
                            {prod?.categoria || 'Não Classificado'}
                          </span>
                        </td>
                        <td style={{ padding: '15px 20px' }}>
                          <StockHealthBar current={prod?.estoque_atual} min={prod?.estoque_minimo} />
                        </td>
                        <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={() => openEditModal(prod)} style={{ backgroundColor: '#fff', border: '1px solid #cbd5e1', color: '#475569', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }} title="Completar Informações">
                              <FaEdit /> Editar Info
                            </button>
                            <button onClick={() => openAdjustModal(prod)} style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', color: '#4f46e5', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                              <FaArrowDown /> Consumir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredProdutos.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', fontSize: '1.1rem' }}>
                          Nenhum item encontrado no inventário.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'requisicoes' && (
        <div className="fade-in-up">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={() => setIsReqModalOpen(true)} style={{ backgroundColor: '#4f46e5', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)' }}>
              <FaPlus /> Solicitar Nova Compra
            </button>
          </div>
          
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            {loadingList ? (
              <div style={{ padding: '50px', textAlign: 'center' }}><FaSpinner className="fa-spin" size={30} color="#4f46e5" /></div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Solicitação</th>
                    <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', width: '35%' }}>Descrição</th>
                    <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Prioridade</th>
                    <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>Status no Compras</th>
                    <th style={{ padding: '15px 20px', color: '#475569', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center' }}>Rastreabilidade</th>
                  </tr>
                </thead>
                <tbody>
                  {requisicoes.map(req => (
                    <tr key={req.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '15px 20px' }}>
                        <div style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#0f172a', fontSize: '1rem' }}>REQ-{String(req.id).padStart(4, '0')}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req?.data_solicitacao ? new Date(req.data_solicitacao).toLocaleDateString('pt-BR') : '-'}</div>
                      </td>
                      <td style={{ padding: '15px 20px' }}>
                        <div style={{ color: '#475569', fontWeight: 500, fontSize: '0.9rem', marginBottom: 4 }}>{req?.descricao}</div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, color: '#64748b', fontWeight: 600 }}>
                          CC: {req?.centro_custo || 'N/I'}
                        </div>
                      </td>
                      <td style={{ padding: '15px 20px' }}>
                        <span style={{ backgroundColor: req?.prioridade === 'urgente' ? '#fef2f2' : (req?.prioridade === 'alta' ? '#fffbeb' : '#f8fafc'), color: req?.prioridade === 'urgente' ? '#ef4444' : (req?.prioridade === 'alta' ? '#d97706' : '#64748b'), padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', border: '1px solid currentColor' }}>
                          {req?.prioridade}
                        </span>
                      </td>
                      <td style={{ padding: '15px 20px' }}>
                        {req.numero_po ? (
                          <div>
                            <span style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 700 }}>
                              Pedido Emitido
                            </span>
                            <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 4, fontWeight: 700 }}>
                              {req.numero_po}
                            </div>
                          </div>
                        ) : (
                          <span style={{ backgroundColor: req?.status === 'pendente' ? '#fef3c7' : '#f1f5f9', color: req?.status === 'pendente' ? '#b45309' : '#64748b', padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>
                            {req?.status === 'em_cotacao' ? 'Em Cotação' : req?.status}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                        <button 
                          onClick={() => openReqDetails(req)}
                          style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', color: '#0f172a', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}
                        >
                          <FaEye /> Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                  {requisicoes.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Nenhuma requisição interna registrada.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* --- MODAL DE DETALHES E RASTREIO DA REQUISIÇÃO (NOVO) --- */}
      <ModalShell
        open={reqDetailsModalOpen} onClose={() => setReqDetailsModalOpen(false)} title={<><FaSearch color="#0ea5e9" /> Rastreio de Solicitação</>} maxWidth={600}
        footer={<button style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }} onClick={() => setReqDetailsModalOpen(false)}>Fechar</button>}
      >
        {selectedReq && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Identificador</div>
                <div style={{ fontSize: '1.4rem', color: '#0f172a', fontWeight: 800, fontFamily: 'monospace' }}>REQ-{String(selectedReq.id).padStart(4, '0')}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Data de Abertura</div>
                <div style={{ fontSize: '1rem', color: '#334155', fontWeight: 600 }}>{selectedReq.data_solicitacao ? new Date(selectedReq.data_solicitacao).toLocaleDateString('pt-BR') : '-'}</div>
              </div>
            </div>

            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>O que foi pedido:</div>
              <div style={{ color: '#0f172a', fontSize: '1rem', lineHeight: '1.5' }}>{selectedReq.descricao}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
               <div style={{ padding: '15px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                 <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Solicitante</div>
                 <div style={{ fontSize: '1rem', color: '#0f172a', fontWeight: 600 }}>{selectedReq.solicitante_nome || 'N/I'}</div>
               </div>
               <div style={{ padding: '15px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                 <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Centro de Custo</div>
                 <div style={{ fontSize: '1rem', color: '#0f172a', fontWeight: 600 }}>{selectedReq.centro_custo || 'Não informado'}</div>
               </div>
            </div>

            {/* SEÇÃO DE STATUS DO DEPARTAMENTO DE COMPRAS */}
            <h3 style={{ margin: '10px 0 0 0', fontSize: '1.1rem', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10 }}>Andamento em Compras</h3>
            
            {selectedReq.numero_po ? (
              <div style={{ background: '#ecfdf5', padding: '20px', borderRadius: '12px', border: '1px solid #10b981' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#047857', marginBottom: 15 }}>
                  <FaCheckCircle size={24} />
                  <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>Pedido de Compra Gerado!</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#065f46', fontWeight: 700, marginBottom: 4 }}><FaTag/> Número PO</div>
                    <div style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: 800, fontFamily: 'monospace' }}>{selectedReq.numero_po}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#065f46', fontWeight: 700, marginBottom: 4 }}>Status Atual do Pedido</div>
                    <div style={{ fontSize: '1rem', color: '#0f172a', fontWeight: 700, textTransform: 'uppercase' }}>{selectedReq.po_status}</div>
                  </div>
                </div>
                <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid #a7f3d0' }}>
                  <div style={{ fontSize: '0.85rem', color: '#065f46', fontWeight: 700, marginBottom: 4 }}><FaBuilding/> Fornecedor Escolhido</div>
                  <div style={{ fontSize: '1rem', color: '#0f172a', fontWeight: 700 }}>{selectedReq.fornecedor_nome || 'N/I'}</div>
                </div>
              </div>
            ) : (
              <div style={{ background: '#fef3c7', padding: '20px', borderRadius: '12px', border: '1px solid #f59e0b', textAlign: 'center' }}>
                <FaExclamationCircle size={30} color="#d97706" style={{ marginBottom: 10 }} />
                <h4 style={{ margin: '0 0 5px 0', color: '#92400e', fontSize: '1.1rem' }}>Em Processamento</h4>
                <p style={{ margin: 0, color: '#b45309', fontSize: '0.95rem' }}>O departamento de compras ainda está avaliando esta requisição ou realizando cotações de preços.</p>
              </div>
            )}
          </div>
        )}
      </ModalShell>

      {/* --- MODAL EDITAR INFORMAÇÕES DO PRODUTO --- */}
      <ModalShell open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={<><FaEdit color="#4f46e5" /> Completar Dados do Item</>} maxWidth={600} footer={<><button style={{ backgroundColor: 'transparent', border: '1px solid #cbd5e1', color: '#475569', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }} onClick={() => setIsEditModalOpen(false)} disabled={savingEdit}>Cancelar</button><button style={{ backgroundColor: '#4f46e5', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? <FaSpinner className="fa-spin" /> : <><FaSave /> Salvar Informações</>}</button></>}>
        <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '12px 15px', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}><FaExclamationCircle size={20} /><span>Complete as informações deste item recebido para manter o inventário organizado.</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Nome do Produto *</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} value={formEditProduto.nome} onChange={e => setFormEditProduto({ ...formEditProduto, nome: e.target.value })} autoFocus /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Código SKU</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} placeholder="Ex: OLEO-5W40" value={formEditProduto.codigo_sku} onChange={e => setFormEditProduto({ ...formEditProduto, codigo_sku: e.target.value })} /></div>
            <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Categoria</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} placeholder="Ex: Lubrificantes" value={formEditProduto.categoria} onChange={e => setFormEditProduto({ ...formEditProduto, categoria: e.target.value })} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Unid. Medida</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} placeholder="UN, L, KG" value={formEditProduto.unidade_medida} onChange={e => setFormEditProduto({ ...formEditProduto, unidade_medida: e.target.value.toUpperCase() })} maxLength={4} /></div>
            <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Estoque Mínimo</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} type="number" placeholder="0" value={formEditProduto.estoque_minimo} onChange={e => setFormEditProduto({ ...formEditProduto, estoque_minimo: e.target.value })} /></div>
          </div>
        </div>
      </ModalShell>

      {/* --- MODAL BAIXA / MOVIMENTAÇÃO DE ESTOQUE --- */}
      <ModalShell open={isAdjustModalOpen} onClose={() => setIsAdjustModalOpen(false)} title={<><FaBoxOpen color="#b91c1c" /> Registrar Consumo / Baixa</>} maxWidth={550} footer={<><button style={{ backgroundColor: 'transparent', border: '1px solid #cbd5e1', color: '#475569', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }} onClick={() => setIsAdjustModalOpen(false)} disabled={savingAdjust}>Cancelar</button><button style={{ backgroundColor: adjustFormData.tipo === 'saida' ? '#ef4444' : '#10b981', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleSaveAdjustment} disabled={savingAdjust}>{savingAdjust ? <FaSpinner className="fa-spin" /> : <><FaCheckCircle /> Confirmar Operação</>}</button></>}>
        {selectedProduct && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px dashed #cbd5e1' }}><div style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, marginBottom: '5px' }}>Item Selecionado</div><div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>{selectedProduct.nome}</div><div style={{ marginTop: '10px', display: 'flex', gap: '15px' }}><span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600 }}>Saldo Disponível: {selectedProduct.estoque_atual} {selectedProduct.unidade_medida}</span></div></div>
            <div style={{ display: 'flex', background: '#f1f5f9', padding: '6px', borderRadius: '12px' }}><div onClick={() => setAdjustFormData({ ...adjustFormData, tipo: 'saida' })} style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', background: adjustFormData.tipo === 'saida' ? '#fff' : 'transparent', color: adjustFormData.tipo === 'saida' ? '#b91c1c' : '#64748b', boxShadow: adjustFormData.tipo === 'saida' ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none' }}><FaArrowDown /> Consumo (Saída)</div><div onClick={() => setAdjustFormData({ ...adjustFormData, tipo: 'entrada' })} style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', background: adjustFormData.tipo === 'entrada' ? '#fff' : 'transparent', color: adjustFormData.tipo === 'entrada' ? '#047857' : '#64748b', boxShadow: adjustFormData.tipo === 'entrada' ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none' }}><FaArrowUp /> Correção Manual</div></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Qtd ({selectedProduct.unidade_medida}) *</label><input type="number" step="0.01" min="0.01" style={{ width: '100%', padding: '10px', border: `1px solid ${adjustFormData.tipo === 'entrada' ? '#10b981' : '#ef4444'}`, borderRadius: '6px', fontSize: '1.2rem', fontWeight: 'bold', color: adjustFormData.tipo === 'entrada' ? '#047857' : '#b91c1c', outline: 'none' }} value={adjustFormData.quantidade} onChange={e => setAdjustFormData({ ...adjustFormData, quantidade: e.target.value })} autoFocus /></div>
              {adjustFormData.tipo === 'entrada' && (<div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Custo Unit. (R$)</label><input type="number" step="0.01" placeholder="0.00" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1.1rem', outline: 'none' }} value={adjustFormData.custo_unitario} onChange={e => setAdjustFormData({ ...adjustFormData, custo_unitario: e.target.value })} /></div>)}
            </div>
            <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Qual o destino ou motivo? *</label><input style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} placeholder={adjustFormData.tipo === 'saida' ? "Ex: Uso no Caminhão Placa ABC-1234" : "Ex: Contagem de inventário físico"} value={adjustFormData.motivo} onChange={e => setAdjustFormData({ ...adjustFormData, motivo: e.target.value })} /></div>
          </div>
        )}
      </ModalShell>

      {/* --- MODAL REQUISIÇÃO NOVA --- */}
      <ModalShell open={isReqModalOpen} onClose={() => setIsReqModalOpen(false)} title={<><FaClipboardList color="#d97706" /> Solicitação de Compra Interna</>} maxWidth={600} footer={<><button style={{ backgroundColor: 'transparent', border: '1px solid #cbd5e1', color: '#475569', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }} onClick={() => setIsReqModalOpen(false)} disabled={savingReq}>Cancelar</button><button style={{ backgroundColor: '#d97706', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleSaveRequisicao} disabled={savingReq}>{savingReq ? <FaSpinner className="fa-spin" /> : <><FaPaperPlane /> Enviar para Compras</>}</button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div><label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Descreva sua necessidade de forma clara *</label><textarea rows="5" placeholder="Ex: Precisamos de 5 baldes de óleo 5W40 para a frota..." style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none', resize: 'vertical' }} value={formRequisicao.descricao} onChange={e => setFormRequisicao({ ...formRequisicao, descricao: e.target.value })} autoFocus /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Centro de Custo</label>
              <select style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', outline: 'none' }} value={formRequisicao.centro_custo} onChange={e => setFormRequisicao({ ...formRequisicao, centro_custo: e.target.value })}>
                <option value="">Selecione um CC...</option>
                {centrosCusto.map(cc => (<option key={cc.id} value={cc.codigo}>{cc.codigo} - {cc.nome_area}</option>))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Prioridade</label>
              <select style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1.05rem', fontWeight: 600, outline: 'none', color: formRequisicao.prioridade === 'urgente' ? '#b91c1c' : (formRequisicao.prioridade === 'alta' ? '#b45309' : '#0f172a') }} value={formRequisicao.prioridade} onChange={e => setFormRequisicao({ ...formRequisicao, prioridade: e.target.value })}>
                <option value="media">🟢 Média (Rotina)</option><option value="alta">🟠 Alta (Semana)</option><option value="urgente">🔴 URGENTE</option>
              </select>
            </div>
          </div>
        </div>
      </ModalShell>
    </div>
  );
}